import { displayDepartment, type Department } from "../api/departments";

/** Label for the trailing group of shared / unit-less departments. */
export const COMMON_UNIT_LABEL = "Common";

/**
 * The dropdown group a department belongs to: its unit, or "Common" for shared
 * (`is_common`) or unit-less departments.
 */
export function unitGroupLabel(
  dept: Pick<Department, "unit" | "is_common">,
): string {
  if (dept.is_common || !dept.unit) return COMMON_UNIT_LABEL;
  return dept.unit;
}

const byDisplayName = (a: Department, b: Department) =>
  displayDepartment(a.name).localeCompare(displayDepartment(b.name));

/**
 * Group departments by unit for sectioned dropdowns. Real units come first
 * (alphabetical), each sorted by display name; shared / unit-less departments
 * collapse into a single trailing "Common" group.
 */
export function groupDepartmentsByUnit(
  departments: Department[],
): { unit: string; departments: Department[] }[] {
  const byUnit = new Map<string, Department[]>();
  const common: Department[] = [];
  for (const dept of departments ?? []) {
    if (dept.is_common || !dept.unit) {
      common.push(dept);
    } else {
      const arr = byUnit.get(dept.unit) ?? [];
      arr.push(dept);
      byUnit.set(dept.unit, arr);
    }
  }
  const groups = Array.from(byUnit.keys())
    .sort((a, b) => a.localeCompare(b))
    .map((unit) => ({
      unit,
      departments: (byUnit.get(unit) ?? []).slice().sort(byDisplayName),
    }));
  if (common.length > 0) {
    groups.push({
      unit: COMMON_UNIT_LABEL,
      departments: common.slice().sort(byDisplayName),
    });
  }
  return groups;
}

/** True when grouping yields more than one section (so headers are worth showing). */
export function hasMultipleUnitGroups(departments: Department[]): boolean {
  return groupDepartmentsByUnit(departments).length > 1;
}

/**
 * Departments flattened into group order (real units alphabetical, "Common"
 * last). MUI Autocomplete's `groupBy` requires options pre-sorted by group, so
 * pass these as the option list alongside a `groupBy` that returns
 * `unitGroupLabel(dept)`.
 */
export function sortDepartmentsByUnit(departments: Department[]): Department[] {
  return groupDepartmentsByUnit(departments).flatMap((g) => g.departments);
}

/**
 * Option list for the Unit filter (SuggestionFilters): distinct real unit
 * names sorted, "Common" last — and only when a department actually
 * resolves to it (mirrors `groupDepartmentsByUnit`, which only emits a
 * Common group when common/unit-less departments exist; `metadata.unit`
 * on rows is always a real unit or absent, never the literal "Common",
 * so an unconditional "Common" option would silently match zero rows on
 * an all-real-units account). Resolution goes through `unitGroupLabel`
 * so it matches row-side unit resolution (`resolveSuggestionUnit`,
 * types/ess.ts) and the Department dropdown grouping exactly.
 */
export function buildUnitFilterOptions(departments: Department[]): string[] {
  const units = new Set<string>();
  let hasCommon = false;
  (departments ?? []).forEach((d) => {
    if (!d?.name) return;
    const label = unitGroupLabel(d);
    if (label === COMMON_UNIT_LABEL) {
      hasCommon = true;
    } else {
      units.add(label);
    }
  });
  const sorted = Array.from(units).sort();
  return hasCommon ? [...sorted, COMMON_UNIT_LABEL] : sorted;
}

export interface DepartmentSelectOption {
  label: string;
  value: string;
  group?: string;
}

/**
 * Build grouped <Select> options for a department list. Each option gets a
 * `group` (its unit) only when more than one group exists, so single-unit
 * accounts render a flat list with no lone header. Labels run through
 * displayDepartment().
 */
export function buildDepartmentSelectOptions(
  departments: Department[],
): DepartmentSelectOption[] {
  const groups = groupDepartmentsByUnit(departments);
  const multi = groups.length > 1;
  return groups.flatMap((g) =>
    g.departments.map((d) => ({
      label: displayDepartment(d.name),
      value: d.name,
      ...(multi ? { group: g.unit } : {}),
    })),
  );
}

/**
 * The units a person can belong to, derived from the department list: the
 * distinct real units, sorted. Shared (`is_common`) and unit-less
 * departments contribute nothing — "Common" is a department grouping, not
 * a unit anyone works in. A unit with no departments yet has no option.
 */
export function buildUserUnitOptions(
  departments: { unit?: string | null; is_common?: boolean | null }[],
): string[] {
  const units = new Set<string>();
  (departments ?? []).forEach((d) => {
    const unit = d?.unit?.trim();
    if (unit && !d.is_common) units.add(unit);
  });
  return Array.from(units).sort((a, b) => a.localeCompare(b));
}
